import { useState } from "react";
import Modal from "../ui/Modal.jsx";
import Button from "../ui/Button.jsx";
import { useToast } from "../../hooks/useToast.js";
import { mapAdminError } from "../../utils/adminApiErrors.js";

function looksLikeEmail(value) {
  const v = String(value || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function InviteUserModal({ onClose, onInvited }) {
  const { show } = useToast();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setInlineError("");
    if (!looksLikeEmail(email)) {
      setInlineError("Please check the email format");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: email.trim().toLowerCase(),
        is_admin: isAdmin,
      };
      if (firstName.trim() || lastName.trim()) {
        payload.name = { first: firstName.trim(), last: lastName.trim() };
      }

      const res = await fetch("/api/admin/users", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 400 && body?.error === "invalid_body") {
          setInlineError("Please check the email format");
        } else if (res.status === 409 && body?.error === "email_taken") {
          setInlineError("Email is already in use");
        } else {
          show(mapAdminError(body?.error), "error");
        }
        return;
      }

      if (body?.invite_email_status === "failed") {
        show("User created but invite email failed. Open the user record and click 'Send password reset' to retry.", "warning");
      } else {
        show(`Invite sent to ${payload.email}. They'll receive an email to set up their account.`, "success");
      }

      onInvited?.();
      onClose?.();
    } catch {
      show("Failed to invite user. Try again.", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open onClose={onClose} titleId="invite-user-title" size="md">
      <form onSubmit={handleSubmit} style={{ padding: "20px 24px" }}>
        <h3 id="invite-user-title" style={{ margin: "0 0 14px", fontSize: "18px", fontWeight: 600 }}>
          Invite user
        </h3>

        <div style={{ marginBottom: "12px" }}>
          <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              width: "100%",
              padding: "10px 12px",
              fontSize: "14px",
              borderRadius: "4px",
              border: "1px solid var(--border-bright)",
              background: "var(--bg-elevated)",
              color: "var(--text-primary)",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>First name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid var(--border-bright)",
                background: "var(--bg-elevated)",
                color: "var(--text-primary)",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>Last name</label>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid var(--border-bright)",
                background: "var(--bg-elevated)",
                color: "var(--text-primary)",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: "12px" }}>
          <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>Initial role</div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginRight: "16px" }}>
            <input type="radio" checked={!isAdmin} onChange={() => setIsAdmin(false)} />
            User
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <input type="radio" checked={isAdmin} onChange={() => setIsAdmin(true)} />
            Admin
          </label>
        </div>

        {inlineError ? (
          <p role="alert" style={{ margin: "0 0 12px", color: "var(--critical)", fontSize: "13px" }}>
            {inlineError}
          </p>
        ) : null}

        <div style={{ display: "flex", gap: "10px" }}>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={loading}>
            Send invite
          </Button>
        </div>
      </form>
    </Modal>
  );
}
